import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  getWorkItem,
  listWorkItemsForWorkArea,
  reconcileWorkItem,
  type GitHubWorkItem,
} from '@/lib/api';
import { WorkAreaDriftPanel } from './WorkAreaDriftPanel';

const EMPTY_WORK_ITEMS_MESSAGE =
  "No GitHub work items found in this Work Area's Projects. Add a GitHub Project to this Work Area in its detail page, or wait for webhook ingestion.";

interface WorkAreaWorkbenchProps {
  workAreaId: string;
}

function formatDate(value: string | null): string {
  return value ? value.slice(0, 19).replace('T', ' ') : 'Never';
}

function githubIssueUrl(workItem: GitHubWorkItem): string {
  return `https://github.com/${encodeURIComponent(workItem.githubRepoOwner)}/${encodeURIComponent(workItem.githubRepoName)}/issues/${String(workItem.githubIssueNumber)}`;
}

function repoLabel(workItem: GitHubWorkItem): string {
  return `${workItem.githubRepoOwner}/${workItem.githubRepoName}`;
}

function mutationErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to reconcile work item.';
}

export function WorkAreaWorkbench({ workAreaId }: WorkAreaWorkbenchProps): React.ReactElement {
  const queryClient = useQueryClient();

  const workItemsQuery = useQuery({
    queryKey: ['work-area-work-items', workAreaId],
    queryFn: () => listWorkItemsForWorkArea(workAreaId),
  });

  const workItemIds = useMemo(
    () => workItemsQuery.data?.workItems.map(workItem => workItem.id) ?? [],
    [workItemsQuery.data?.workItems]
  );

  const linkedPrCountsQuery = useQuery({
    queryKey: ['work-area-work-item-pr-counts', workAreaId, workItemIds],
    queryFn: async () => {
      const details = await Promise.all(workItemIds.map(workItemId => getWorkItem(workItemId)));
      const counts: Record<string, number> = {};
      for (const detail of details) {
        counts[detail.workItem.id] = detail.pullRequestLinks.length;
      }
      return counts;
    },
    enabled: workItemIds.length > 0,
    refetchInterval: 30_000,
  });

  const reconcileMutation = useMutation({
    mutationFn: (workItemId: string) => reconcileWorkItem(workItemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-area-work-items', workAreaId] });
      void queryClient.invalidateQueries({
        queryKey: ['work-area-work-item-pr-counts', workAreaId],
      });
    },
  });

  if (workItemsQuery.isLoading) {
    return (
      <div className="space-y-3">
        <WorkAreaDriftPanel workAreaId={workAreaId} />
        <div className="rounded-md border border-border bg-surface-elevated px-3 py-8 text-center text-sm text-text-secondary">
          Loading GitHub work items…
        </div>
      </div>
    );
  }

  if (workItemsQuery.isError) {
    return (
      <div className="space-y-3">
        <WorkAreaDriftPanel workAreaId={workAreaId} />
        <div className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
          Failed to load GitHub work items
          {workItemsQuery.error ? `: ${workItemsQuery.error.message}` : '.'}
        </div>
      </div>
    );
  }

  const workItems = workItemsQuery.data?.workItems ?? [];

  if (workItems.length === 0) {
    return (
      <div className="space-y-3">
        <WorkAreaDriftPanel workAreaId={workAreaId} />
        <div className="rounded-md border border-border bg-surface-elevated px-3 py-8 text-center text-sm text-text-secondary">
          {EMPTY_WORK_ITEMS_MESSAGE}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <WorkAreaDriftPanel workAreaId={workAreaId} />
      {reconcileMutation.isError && (
        <div className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
          {mutationErrorMessage(reconcileMutation.error)}
        </div>
      )}
      <div className="overflow-auto rounded-md border border-border bg-surface-elevated">
        <table className="w-full min-w-[76rem] table-fixed text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-secondary">
              <th className="w-[24%] px-3 py-2 font-medium">Title</th>
              <th className="w-[16%] px-3 py-2 font-medium">Repo</th>
              <th className="w-[7%] px-3 py-2 font-medium">Issue #</th>
              <th className="w-[8%] px-3 py-2 font-medium">State</th>
              <th className="w-[16%] px-3 py-2 font-medium">Labels</th>
              <th className="w-[8%] px-3 py-2 text-right font-medium">Linked PRs</th>
              <th className="w-[12%] px-3 py-2 font-medium">Last reconciled</th>
              <th className="w-[9%] px-3 py-2 text-right font-medium">Reconcile</th>
            </tr>
          </thead>
          <tbody>
            {workItems.map(workItem => {
              const isReconciling =
                reconcileMutation.isPending && reconcileMutation.variables === workItem.id;
              const linkedPrCount = linkedPrCountsQuery.data?.[workItem.id];

              return (
                <tr key={workItem.id} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-2">
                    <a
                      href={githubIssueUrl(workItem)}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate font-medium text-primary hover:underline"
                      title={workItem.title}
                    >
                      {workItem.title}
                    </a>
                  </td>
                  <td
                    className="truncate px-3 py-2 text-text-secondary"
                    title={repoLabel(workItem)}
                  >
                    {repoLabel(workItem)}
                  </td>
                  <td className="px-3 py-2 font-mono text-text-secondary">
                    #{workItem.githubIssueNumber}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={workItem.state === 'open' ? 'default' : 'outline'}>
                      {workItem.state}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {workItem.labels.length === 0 ? (
                      <span className="text-text-tertiary">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {workItem.labels.map(label => (
                          <Badge key={label} variant="outline">
                            {label}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-text-secondary">
                    {linkedPrCountsQuery.isError
                      ? '-'
                      : linkedPrCount === undefined
                        ? '…'
                        : linkedPrCount}
                  </td>
                  <td
                    className="truncate px-3 py-2 text-text-secondary"
                    title={workItem.lastReconciledAt ?? undefined}
                  >
                    {formatDate(workItem.lastReconciledAt)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(): void => {
                        reconcileMutation.mutate(workItem.id);
                      }}
                      disabled={reconcileMutation.isPending}
                    >
                      {isReconciling ? '…' : 'Reconcile'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
